/**
 * WebSocket service for real-time communication with Bili engine
 * Manages WebSocket connection, authentication, and message handling
 */

import { getAuth } from 'firebase/auth';

import logger from '../utils/logger';

const ENGINE_WS_URL = process.env.REACT_APP_ENGINE_WS_URL;

// Error types for different failure scenarios
export const WS_ERROR_TYPES = {
    PERMISSION_DENIED: 'permission_denied',
    CONNECTION_ERROR: 'connection_error',
    AUTH_ERROR: 'auth_error',
};

class WebSocketService {
    constructor() {
        this.ws = null;
        this.connected = false;
        this.connecting = false;
        this.messageHandlers = new Map();
        this.currentThreadId = null;
        this.currentPersona = null; // Persona route segment (e.g. "expert")
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.reconnectDelay = 1000;
        this.messageQueue = [];
        this.pendingMessages = new Map(); // Track messages awaiting response
        this.lastErrorType = null; // Track the type of last error
        this.reconnectTimer = null; // Guard against overlapping reconnect attempts
        this.connectPromise = null; // In-flight connect() attempt, for de-duping overlapping calls
        this.requestedThreadId = null; // Target of the current/last connect() request
        this.requestedPersona = null; // Target persona of the current/last connect() request

        // Clean up WebSocket on page unload to prevent stale connections
        if (typeof window !== 'undefined') {
            window.addEventListener('beforeunload', () => {
                if (this.ws) {
                    this.ws.close(1000, 'Page unload');
                    this.ws = null;
                    this.connected = false;
                    this.connecting = false;
                }
            });
        }
    }

    /**
     * Connect to WebSocket server with authentication.
     *
     * Overlapping calls to the SAME target are de-duped: a connect already in
     * flight (or already established) for the same {threadId, persona} is
     * reused rather than tearing the socket down and opening a second one.
     * Without this guard, React StrictMode's double effect mount (dev) and any
     * rapid re-entry produce a connect → disconnect → connect churn that piles
     * up sockets and leaves a brief not-connected window — which is exactly
     * what races the Advanced Query auto-send. Callers that need to be sure the
     * socket is truly open before sending should await {@link whenReady}.
     *
     * @param {string} threadId - Optional thread ID to connect to specific conversation
     * @param {string} persona - Optional persona route segment (e.g. "expert")
     *   selecting a Bili persona: /bili/{persona}/{threadId}
     * @returns {Promise<void>}
     */
    async connect(threadId = null, persona = null) {
        const sameTarget =
            this.requestedThreadId === threadId &&
            this.requestedPersona === persona;

        // A connection to the same target is already establishing — hand back
        // the in-flight attempt instead of opening a second socket.
        if (sameTarget && this.connecting && this.connectPromise) {
            return this.connectPromise;
        }
        // Already connected to the same target — nothing to do.
        if (sameTarget && this.connected) {
            return Promise.resolve();
        }

        this.requestedThreadId = threadId;
        this.requestedPersona = persona;
        this.connectPromise = this._openConnection(threadId, persona);
        return this.connectPromise;
    }

    /**
     * Tear down any existing socket and open a fresh authenticated connection.
     * Internal — callers use {@link connect}, which de-dupes overlapping calls.
     * @param {string} threadId
     * @param {string} persona
     * @returns {Promise<void>}
     */
    async _openConnection(threadId = null, persona = null) {
        // Cancel any pending reconnect timer to prevent overlapping attempts (Task #126)
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }

        // If already connected or connecting, clean up first to avoid stale state
        if (this.ws) {
            try {
                this.ws.close(1000, 'Reconnecting');
            } catch {
                // Ignore close errors
            }
            this.ws = null;
        }

        // Reset state flags
        this.connected = false;
        this.connecting = true;
        this.lastErrorType = null;

        try {
            // Get Firebase auth token
            const auth = getAuth();
            const user = auth.currentUser;

            if (!user) {
                throw new Error('User not authenticated');
            }

            // Force refresh the token to ensure it's valid after page refresh
            const token = await user.getIdToken(true);

            // Build WebSocket URL (ENGINE_WS_URL already includes the /bili
            // path). An optional persona segment selects a Bili persona route
            // (/bili/{persona}/{threadId}); the engine parses it at connect
            // time to pick the persona for this session.
            let wsUrl = ENGINE_WS_URL;
            if (persona) {
                wsUrl += `/${persona}`;
            }
            this.currentPersona = persona;
            if (threadId) {
                wsUrl += `/${threadId}`;
                this.currentThreadId = threadId;
            }

            // Create WebSocket connection with auth header
            this.ws = new WebSocket(wsUrl, [token]);

            // Set up event handlers
            this.ws.onopen = this._handleOpen.bind(this);
            this.ws.onmessage = this._handleMessage.bind(this);
            this.ws.onerror = this._handleError.bind(this);
            this.ws.onclose = this._handleClose.bind(this);

            // Wait for connection to establish
            await new Promise((resolve, reject) => {
                const timeout = setTimeout(() => {
                    reject(new Error('WebSocket connection timeout'));
                }, 10000);

                this.ws.addEventListener(
                    'open',
                    () => {
                        clearTimeout(timeout);
                        resolve();
                    },
                    { once: true }
                );

                this.ws.addEventListener(
                    'error',
                    (error) => {
                        clearTimeout(timeout);
                        reject(error);
                    },
                    { once: true }
                );
            });
        } catch (error) {
            this.connecting = false;

            // Determine error type based on the failure
            // 403 Forbidden typically causes immediate WebSocket failure
            if (error.message?.includes('not authenticated')) {
                this.lastErrorType = WS_ERROR_TYPES.AUTH_ERROR;
            } else {
                // Check if this might be a permission denied (403)
                // WebSocket 403 failures happen immediately during handshake
                this.lastErrorType = WS_ERROR_TYPES.CONNECTION_ERROR;
            }

            logger.error('Failed to connect to WebSocket:', error);
            throw error;
        }
    }

    /**
     * True only when the CURRENT socket is actually open.
     *
     * This deliberately checks the live `readyState` rather than the
     * `this.connected` flag. During connect churn (React StrictMode's
     * connect→disconnect→connect, an overlapping reconnect, etc.) a previous
     * socket's `open` event can set `connected = true` while `this.ws` has
     * already been replaced by a newer socket that is still CONNECTING. Callers
     * that are about to `send()` must trust the live socket state, or they hit
     * "Failed to execute 'send' on 'WebSocket': Still in CONNECTING state".
     * @returns {boolean}
     */
    _isSocketOpen() {
        return !!this.ws && this.ws.readyState === WebSocket.OPEN;
    }

    /**
     * Resolve once the socket is actually open. Use this before issuing a
     * command (e.g. an auto-sent query) so it does not fire during the brief
     * window where a connect has been requested but the handshake has not yet
     * completed — the situation that otherwise throws "WebSocket not connected".
     * @param {number} timeout - Max time to wait for the open event (ms)
     * @returns {Promise<void>}
     */
    whenReady(timeout = 10000) {
        if (this._isSocketOpen()) {
            return Promise.resolve();
        }
        return new Promise((resolve, reject) => {
            let timer;
            const cleanup = () => {
                clearTimeout(timer);
                this.off('open', onOpen);
            };
            const onOpen = () => {
                // A stale socket's open event may fire while the current
                // this.ws is still CONNECTING — only resolve once the live
                // socket is actually open.
                if (!this._isSocketOpen()) return;
                cleanup();
                resolve();
            };
            timer = setTimeout(() => {
                cleanup();
                reject(new Error('WebSocket not ready'));
            }, timeout);
            this.on('open', onOpen);
            // Guard against the socket opening between the check above and the
            // handler registration.
            if (this._isSocketOpen()) {
                onOpen();
            }
        });
    }

    /**
     * Get the last error type that occurred
     * @returns {string|null} Error type or null
     */
    getLastErrorType() {
        return this.lastErrorType;
    }

    /**
     * Clear the last error type
     */
    clearLastError() {
        this.lastErrorType = null;
    }

    /**
     * Disconnect from WebSocket server
     */
    disconnect() {
        // Cancel any pending reconnect timer (Task #126)
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        if (this.ws) {
            this.ws.close(1000, 'Client disconnect');
            this.ws = null;
            this.connected = false;
            this.currentThreadId = null;
            this.currentPersona = null;
        }
        // Forget the in-flight/last connect target so a subsequent connect() to
        // the same target is not incorrectly treated as a no-op.
        this.connecting = false;
        this.connectPromise = null;
        this.requestedThreadId = null;
        this.requestedPersona = null;
        // Clear pending messages to avoid sending stale data on reconnect (Task #125)
        this.pendingMessages.forEach((pending) => {
            pending.reject(new Error('WebSocket disconnected'));
        });
        this.pendingMessages.clear();
        this.messageQueue = [];
    }

    /**
     * Send a message to the bot
     * @param {string} message - User message text
     * @param {Object} options - Optional extra data (e.g., page_context)
     * @returns {Promise<Object>} Bot response
     */
    async sendMessage(message, options = {}) {
        if (!this._isSocketOpen()) {
            throw new Error('WebSocket not connected');
        }

        const messageId = `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        const payload = {
            type: 'message',
            action: 'send',
            data: { message, ...options },
            message_id: messageId,
        };

        // Send message
        this.ws.send(JSON.stringify(payload));

        // Wait for response
        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                this.pendingMessages.delete(messageId);
                reject(new Error('Message timeout'));
            }, 120000); // 120 second timeout

            this.pendingMessages.set(messageId, {
                resolve: (response) => {
                    clearTimeout(timeout);
                    this.pendingMessages.delete(messageId);
                    resolve(response);
                },
                reject: (error) => {
                    clearTimeout(timeout);
                    this.pendingMessages.delete(messageId);
                    reject(error);
                },
            });
        });
    }

    /**
     * Create a new conversation
     * @returns {Promise<Object>} New conversation data with thread_id
     */
    async createNewConversation() {
        if (!this._isSocketOpen()) {
            throw new Error('WebSocket not connected');
        }

        const messageId = `cmd-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        const payload = {
            type: 'command',
            action: 'new_conversation',
            data: {},
            message_id: messageId,
        };

        this.ws.send(JSON.stringify(payload));

        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                this.pendingMessages.delete(messageId);
                reject(new Error('Command timeout'));
            }, 10000);

            this.pendingMessages.set(messageId, {
                resolve: (response) => {
                    clearTimeout(timeout);
                    this.pendingMessages.delete(messageId);
                    if (response.status === 'success') {
                        this.currentThreadId = response.data.thread_id;
                        resolve(response.data);
                    } else {
                        reject(
                            new Error(
                                response.data?.error ||
                                    'Failed to create conversation'
                            )
                        );
                    }
                },
                reject: (error) => {
                    clearTimeout(timeout);
                    this.pendingMessages.delete(messageId);
                    reject(error);
                },
            });
        });
    }

    /**
     * Switch to a different conversation
     * @param {string} threadId - Thread ID to switch to
     * @returns {Promise<void>}
     */
    async switchConversation(threadId) {
        // For now, we'll disconnect and reconnect with new thread ID
        // This is simpler than using the switch_conversation command
        this.disconnect();
        await this.connect(threadId);
    }

    /**
     * Get current conversation info
     * @returns {Promise<Object>} Current conversation info
     */
    async getCurrentConversation() {
        if (!this._isSocketOpen()) {
            throw new Error('WebSocket not connected');
        }

        const messageId = `cmd-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        const payload = {
            type: 'command',
            action: 'get_current_conversation',
            data: {},
            message_id: messageId,
        };

        this.ws.send(JSON.stringify(payload));

        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                this.pendingMessages.delete(messageId);
                reject(new Error('Command timeout'));
            }, 10000);

            this.pendingMessages.set(messageId, {
                resolve: (response) => {
                    clearTimeout(timeout);
                    this.pendingMessages.delete(messageId);
                    if (response.status === 'success') {
                        resolve(response.data);
                    } else {
                        reject(
                            new Error(
                                response.data?.error ||
                                    'Failed to get conversation'
                            )
                        );
                    }
                },
                reject: (error) => {
                    clearTimeout(timeout);
                    this.pendingMessages.delete(messageId);
                    reject(error);
                },
            });
        });
    }

    /**
     * Register a handler for incoming messages
     * @param {string} type - Message type to handle
     * @param {Function} handler - Handler function
     */
    on(type, handler) {
        if (!this.messageHandlers.has(type)) {
            this.messageHandlers.set(type, []);
        }
        this.messageHandlers.get(type).push(handler);
    }

    /**
     * Unregister a message handler
     * @param {string} type - Message type
     * @param {Function} handler - Handler function to remove
     */
    off(type, handler) {
        if (this.messageHandlers.has(type)) {
            const handlers = this.messageHandlers.get(type);
            const index = handlers.indexOf(handler);
            if (index > -1) {
                handlers.splice(index, 1);
            }
        }
    }

    /**
     * Handle WebSocket open event
     */
    _handleOpen() {
        // Ignore an open event from a stale socket: during connect churn a
        // prior socket can fire 'open' after this.ws has been replaced by a
        // newer, still-connecting socket. Acting on it would flip `connected`
        // true and flush the queue onto a socket that isn't open yet.
        if (!this._isSocketOpen()) {
            return;
        }

        this.connected = true;
        this.connecting = false;
        this.reconnectAttempts = 0;

        // Flush message queue
        while (this.messageQueue.length > 0) {
            const message = this.messageQueue.shift();
            this.ws.send(message);
        }

        // Notify handlers
        this._notifyHandlers('open', {});
    }

    /**
     * Handle incoming WebSocket message
     * @param {MessageEvent} event - WebSocket message event
     */
    _handleMessage(event) {
        try {
            const data = JSON.parse(event.data);

            // Handle responses to pending messages
            if (
                data.in_reply_to &&
                this.pendingMessages.has(data.in_reply_to)
            ) {
                const pending = this.pendingMessages.get(data.in_reply_to);
                if (data.type === 'error') {
                    pending.reject(
                        new Error(data.data?.error || 'Unknown error')
                    );
                } else {
                    pending.resolve(data);
                }
                return;
            }

            // Notify type-specific handlers
            this._notifyHandlers(data.type, data);

            // Always notify 'message' handlers for all messages
            if (data.type !== 'message') {
                this._notifyHandlers('message', data);
            }
        } catch (error) {
            logger.error('Failed to parse WebSocket message:', error);
        }
    }

    /**
     * Handle WebSocket error
     * @param {Event} error - Error event
     */
    _handleError(error) {
        logger.error('WebSocket error:', error);
        this._notifyHandlers('error', error);
    }

    /**
     * Handle WebSocket close event
     * @param {CloseEvent} event - Close event
     */
    _handleClose(event) {
        this.connected = false;
        this.connecting = false;

        // Detect permission denied (403) scenarios
        // Close code 4403 = explicit 403 Forbidden
        // Close code 1008 = Policy Violation (often used for auth failures)
        // Close code 1006 with "403" in reason = HTTP 403 during handshake
        const isPermissionDenied =
            event.code === 4403 ||
            event.code === 1008 ||
            (event.code === 1006 &&
                event.reason?.toLowerCase().includes('403')) ||
            event.reason?.toLowerCase().includes('forbidden') ||
            event.reason?.toLowerCase().includes('permission') ||
            event.reason?.toLowerCase().includes('unauthorized role');

        if (isPermissionDenied) {
            this.lastErrorType = WS_ERROR_TYPES.PERMISSION_DENIED;
        }

        this._notifyHandlers('close', {
            code: event.code,
            reason: event.reason,
            errorType: this.lastErrorType,
        });

        // Don't attempt reconnection for permission denied or normal close
        if (isPermissionDenied || event.code === 1000) {
            return;
        }

        // Attempt reconnection for other errors (with guard against overlapping attempts)
        if (
            this.reconnectAttempts < this.maxReconnectAttempts &&
            !this.connecting &&
            !this.reconnectTimer
        ) {
            this.reconnectAttempts++;
            const delay =
                this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

            this.reconnectTimer = setTimeout(() => {
                this.reconnectTimer = null;
                // Preserve the persona route across reconnects.
                this.connect(this.currentThreadId, this.currentPersona).catch(
                    (err) => {
                        logger.error('Reconnection failed:', err);
                    }
                );
            }, delay);
        }
    }

    /**
     * Notify registered handlers
     * @param {string} type - Event type
     * @param {*} data - Event data
     */
    _notifyHandlers(type, data) {
        if (this.messageHandlers.has(type)) {
            this.messageHandlers.get(type).forEach((handler) => {
                try {
                    handler(data);
                } catch (error) {
                    logger.error(`Error in ${type} handler:`, error);
                }
            });
        }
    }

    /**
     * Get connection status
     * @returns {boolean} True if connected
     */
    isConnected() {
        return this.connected;
    }

    /**
     * Get current thread ID
     * @returns {string|null} Current thread ID
     */
    getThreadId() {
        return this.currentThreadId;
    }
}

// Export singleton instance
export default new WebSocketService();
